import React, { useEffect, useState } from "react";
import {
  Box,
  ButtonStyledTb,
  FormButtonStyled,
  HeadingStyled,
  LabelStyled,
} from "../../../components/styled/Elements";
import { useTranslation } from "react-i18next";
import { Calendar } from "../../../components/styled/AllImages";
import { ProfileEditContainer } from "./styled";
import { Col, Form, Row } from "react-bootstrap";
import ValidatedInput from "../../../components/common/inputFields/ValidatedInput";
import { SelectionDropdown } from "../../../components/styled/FormElements";
import DatePickerDefault from "../../../components/common/inputFields/DatePicker";
import { useForm } from "react-hook-form";
import DocumentUpload from "../../../components/common/uploadFiles/documentUpload";
import ProfileApi from "../../../services/api/profileApi";
import { toast } from "react-toastify";
import ValidationController from "../../../components/common/inputFields/ValidationController";
import { useUser } from "../../../context/userContext";
import { reqFormatDate } from "../../../utils";
import { specializationDropdownList } from "../../../helpers/webConstants";
import { useNavigate } from "react-router-dom";
import { resolveMediaUrl } from "../../../utils/mediaUrl";

const LICENSE_ACCEPT =
  ".pdf,image/png,image/jpeg,image/jpg,image/webp";

/** Safe basename from a media path/URL for display in DocumentUpload. */
const licenseDisplayName = (urlOrPath) => {
  const raw = String(urlOrPath || "").trim();
  if (!raw) return "license";
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    decoded = raw;
  }
  const part = decoded.split("/").pop() || "license";
  return part.toLowerCase();
};

const FalInformation = () => {
  const { t } = useTranslation();
  const { fetchUserInfo, user } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const {
    control,
    reset,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  const specializationValue = watch("specialization");
  const isVisitorSpecialization = Number(specializationValue) === 4;

  useEffect(() => {
    if (!user) return;

    const falLicenseFile = user?.fal_information?.fal_license
      ? resolveMediaUrl(user.fal_information.fal_license)
      : "";

    reset({
      specialization: user?.fal_information?.specialization,
      mothoq_number: user.fal_information?.mothoq_number,
      fal_number: user.fal_information?.fal_number,
      fal_id: user.fal_information?.fal_id,
      fal_exp_date: user.fal_information?.fal_exp_date,
      fal_license: falLicenseFile || "",
    });

    if (falLicenseFile) {
      setSelectedFile({
        name: licenseDisplayName(falLicenseFile),
        url: falLicenseFile,
      });
    } else {
      setSelectedFile(null);
    }
  }, [user, reset]);

  useEffect(() => {
    if (!isVisitorSpecialization) return;
    setValue("fal_number", "");
    setValue("fal_id", "");
    setValue("fal_exp_date", "");
    setValue("fal_license", "");
    setSelectedFile(null);
  }, [isVisitorSpecialization, setValue]);

  const onSubmit = async (dataObj) => {
    try {
      setLoading(true);
      const isVisitor = Number(dataObj.specialization) === 4;
      const formData = new FormData();
      Object.entries(dataObj).forEach(([key, value]) => {
        if (
          isVisitor &&
          ["fal_number", "fal_id", "fal_exp_date", "fal_license"].includes(key)
        ) {
          return;
        }
        if (key === "fal_license") {
          // Only send a newly picked File; keep existing remote URL off FormData
          // so Update without re-upload does not wipe the license.
          if (value instanceof File || value instanceof Blob) {
            formData.append(key, value);
          }
          return;
        }
        if (key === "fal_exp_date") {
          if (value) {
            formData.append(key, `${reqFormatDate(value)}`);
          }
          return;
        }
        if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });
      if (
        !isVisitor &&
        selectedFile instanceof File &&
        !formData.has("fal_license")
      ) {
        formData.append("fal_license", selectedFile);
      }

      const { code, message } = await ProfileApi.saveFalInfo(formData);
      if (code === 200) {
        toast.success(t("Fal_information_updated"));
        fetchUserInfo();
      }
      if (code === 100 || code === 204) {
        toast.error(message);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ProfileEditContainer>
        <HeadingStyled size="24px" className="editProfileHeading">
          {t("FALInformation")}
        </HeadingStyled>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Box className="profileFormContainerInner">
            <Row className="mt-3">
              <Col md={12}>
                <Box className="formEleContainer">
                  <LabelStyled className="labelForm">
                    {t("Specialization")}
                    <span className="staric">*</span>
                  </LabelStyled>
                  <ValidationController
                    name={"specialization"}
                    control={control}
                    errors={errors}
                    validationConfig={{
                      required: `fieldRequired`,
                    }}
                    renderer={({ field }) => (
                      <SelectionDropdown
                        name={field.name}
                        placeholder={t("Specialization")}
                        options={specializationDropdownList}
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                      />
                    )}
                  />
                </Box>
              </Col>
              <Col md={12}>
                <ValidatedInput
                  name="mothoq_number"
                  type="input"
                  fieldType="text"
                  placeholder="EnterMothoqNo"
                  label="MothoqNo"
                  className=""
                  register={register}
                  errors={errors}
                  validationRules={{
                    required: false,
                  }}
                />
              </Col>
              {!isVisitorSpecialization && (
                <>
                  <Col md={12}>
                    <ValidatedInput
                      name="fal_number"
                      type="input"
                      fieldType="text"
                      placeholder="EnterFAL"
                      label="FalNo"
                      className=""
                      staric="staric"
                      register={register}
                      errors={errors}
                      validationRules={{
                        required: true,
                      }}
                    />
                  </Col>
                  <Col md={12}>
                    <ValidatedInput
                      name="fal_id"
                      type="input"
                      fieldType="text"
                      placeholder="EnteryourID"
                      label="id"
                      className=""
                      staric="staric"
                      register={register}
                      errors={errors}
                      validationRules={{
                        required: true,
                      }}
                    />
                  </Col>
                  <Col md={12}>
                    <Box className="formEleContainer">
                      <LabelStyled className="labelForm">
                        {t("FALExpDate")}
                        <span className="staric">*</span>
                      </LabelStyled>
                      <DatePickerDefault
                        control={control}
                        rules={{
                          required: `fieldRequired`,
                        }}
                        name={"fal_exp_date"}
                        type={"input"}
                        placeholder={t("FALExpDate")}
                        icon={Calendar}
                      />
                    </Box>
                  </Col>
                  <Col md={12}>
                    <Box className="formEleContainer">
                      <LabelStyled className="labelForm">
                        {t("UploadFALLicense")}
                        <span className="staric">*</span>
                      </LabelStyled>
                      <ValidationController
                        name={"fal_license"}
                        control={control}
                        errors={errors}
                        validationConfig={{
                          required: `fieldRequired`,
                        }}
                        renderer={({ field }) => (
                          <DocumentUpload
                            UploadImage="UploadFALLicense"
                            UploadSecTxt="ordraganddrop"
                            accept={LICENSE_ACCEPT}
                            onChange={field.onChange}
                            selectedFile={selectedFile}
                            setSelectedFile={setSelectedFile}
                          />
                        )}
                      />
                    </Box>
                  </Col>
                </>
              )}
            </Row>
            <Box className="formBtns">
              <ButtonStyledTb
                className="nextStepBtn"
                type="button"
                bg="var(--white)"
                border="var(--border)"
                color="var(--darkGrey)"
                onClick={() => navigate("/my-profile")}
              >
                {t("Cancel")}
              </ButtonStyledTb>
              <FormButtonStyled
                type="submit"
                bg="var(--themeColorSecondary)"
                disabled={loading}
                isLoading={loading}
                label={t("Update")}
              />
            </Box>
          </Box>
        </Form>
      </ProfileEditContainer>
    </>
  );
};

export default FalInformation;
